import request from '@/config/axios'
import type { PageParam } from '@/types/common'
import type { PageResult } from '@/api/lawyer'

export interface FirmMatterVO {
  id?: number
  caseNo?: string
  firmCaseNo?: string
  customerId: number
  customerName?: string
  leadLawyerId: number
  leadLawyerName?: string
  platformLawyerId?: number
  platformLawyerName?: string
  teamId?: number
  teamName?: string
  title: string
  caseType: string
  description?: string
  practiceStatus?: string
  marketplaceStatus?: string
  currentStage?: string
  source?: string
  partyBName?: string
  amountClaim?: number
  courtName?: string
  courtCaseNo?: string
  riskLevel?: string
  urgency?: string
  billingMode?: string
  tags?: string[]
  province?: string
  city?: string
  district?: string
  address?: string
  closeReason?: string
  closeSummary?: string
  closeTime?: string
  createTime?: string
  nextStatuses?: string[]
}

export interface FirmMatterPageReqVO extends PageParam {
  practiceStatus?: string
  customerId?: number
  leadLawyerId?: number
  firmCaseNo?: string
  title?: string
  source?: string
  riskLevel?: string
  urgency?: string
}

export interface FirmMatterMemberVO {
  id?: number
  caseId?: number
  userId: number
  userName?: string
  role: string
  joinedTime?: string
  remark?: string
}

export interface FirmMatterMemberLogVO {
  id?: number
  caseId?: number
  changeType?: string
  fromUserId?: number
  toUserId?: number
  fromRole?: string
  toRole?: string
  reason?: string
  operatorUserId?: number
  createTime?: string
}

export interface FirmMatterLogVO {
  id?: number
  caseId?: number
  logType?: string
  content?: string
  attachmentUrls?: string[]
  logUserId?: number
  logTime?: string
  createTime?: string
}

export interface FirmMatterCloseResp {
  closed: boolean
  warnNoSignedContract?: boolean
}

export const FirmMatterApi = {
  page: async (params: FirmMatterPageReqVO): Promise<PageResult<FirmMatterVO>> =>
    request.get({ url: '/lawyer/firm/matter/page', params }),
  get: async (id: number): Promise<FirmMatterVO> =>
    request.get({ url: '/lawyer/firm/matter/get', params: { id } }),
  create: async (data: FirmMatterVO): Promise<number> =>
    request.post({ url: '/lawyer/firm/matter/create', data }),
  update: async (data: FirmMatterVO): Promise<boolean> =>
    request.put({ url: '/lawyer/firm/matter/update', data }),
  transition: async (data: { id: number; toStatus: string; remark?: string }): Promise<boolean> =>
    request.put({ url: '/lawyer/firm/matter/transition', data }),

  memberList: async (caseId: number): Promise<FirmMatterMemberVO[]> =>
    request.get({ url: '/lawyer/firm/matter/member/list', params: { caseId } }),
  memberAdd: async (data: {
    caseId: number
    userId: number
    role: string
  }): Promise<boolean> => request.post({ url: '/lawyer/firm/matter/member/add', data }),
  memberRemove: async (data: { id: number }): Promise<boolean> =>
    request.put({ url: '/lawyer/firm/matter/member/remove', data }),
  changeLead: async (data: {
    caseId: number
    newLeadLawyerId: number
    reason: string
    formerLeadRole?: string
  }): Promise<boolean> => request.put({ url: '/lawyer/firm/matter/member/change-lead', data }),
  memberLogPage: async (params: {
    caseId: number
    pageNo: number
    pageSize: number
  }): Promise<PageResult<FirmMatterMemberLogVO>> =>
    request.get({ url: '/lawyer/firm/matter/member/log/page', params }),

  logPage: async (params: {
    caseId: number
    pageNo: number
    pageSize: number
    logType?: string
  }): Promise<PageResult<FirmMatterLogVO>> =>
    request.get({ url: '/lawyer/firm/matter/log/page', params }),
  logCreate: async (data: {
    caseId: number
    logType?: string
    content: string
    attachmentUrls?: string[]
    logTime?: string
  }): Promise<number> => request.post({ url: '/lawyer/firm/matter/log/create', data }),

  close: async (data: {
    id: number
    closeReason: string
    closeSummary: string
    forceCloseWithoutContract?: boolean
  }): Promise<FirmMatterCloseResp> => request.put({ url: '/lawyer/firm/matter/close', data }),
  archive: async (data: { id: number }): Promise<boolean> =>
    request.put({ url: '/lawyer/firm/matter/archive', data }),
  voidMatter: async (data: { id: number; reason: string }): Promise<boolean> =>
    request.put({ url: '/lawyer/firm/matter/void', data }),
  updateFirmCaseNo: async (data: { id: number; firmCaseNo: string }): Promise<boolean> =>
    request.put({ url: '/lawyer/firm/matter/update-firm-case-no', data }),

  transferTeam: async (data: { id: number; toTeamId: number; reason: string }): Promise<boolean> =>
    request.put({ url: '/lawyer/firm/matter/transfer-team', data })
}

export const PRACTICE_STATUS_OPTIONS = [
  { label: '待接案', value: 'INTAKE' },
  { label: '已委托', value: 'RETAINED' },
  { label: '办理中', value: 'ACTIVE' },
  { label: '调解中', value: 'MEDIATION' },
  { label: '审理中', value: 'TRIAL' },
  { label: '执行中', value: 'ENFORCEMENT' },
  { label: '已结案', value: 'CLOSED' },
  { label: '已归档', value: 'ARCHIVED' },
  { label: '作废', value: 'VOID' }
]

export const MEMBER_ROLE_OPTIONS = [
  { label: '主办', value: 'LEAD' },
  { label: '协办', value: 'CO' },
  { label: '助理', value: 'ASSIST' }
]

export const LOG_TYPE_OPTIONS = [
  { label: '备注', value: 'NOTE' },
  { label: '进度', value: 'PROGRESS' },
  { label: '会议', value: 'MEETING' },
  { label: '其他', value: 'OTHER' },
  { label: '系统', value: 'SYSTEM' }
]

export const MATTER_SOURCE_OPTIONS = [
  { label: '所内开案', value: 'FIRM' },
  { label: '在线咨询', value: 'CONSULT' },
  { label: '平台撮合', value: 'MARKETPLACE' },
  { label: '商机撮合', value: 'OPPORTUNITY' },
  { label: '用户自建', value: 'USER' }
]

export const matterSourceLabel = (code?: string) =>
  MATTER_SOURCE_OPTIONS.find((o) => o.value === code)?.label || code || '—'

/** @deprecated 案由已改为系统字典 DICT_TYPE.LAWYER_CASE_TYPE */
export const CASE_TYPE_OPTIONS = [
  { value: 'MARRIAGE_FAMILY', label: '婚姻家事' },
  { value: 'DEBT_RECOVERY', label: '债务纠纷' },
  { value: 'LABOR_DISPUTE', label: '劳动争议' },
  { value: 'CONTRACT_DISPUTE', label: '合同纠纷' },
  { value: 'TORT_DISPUTE', label: '侵权赔偿' },
  { value: 'PROPERTY_RIGHT', label: '物权纠纷' },
  { value: 'OTHER', label: '其他' }
]

export function isMatterLocked(status?: string) {
  return status === 'CLOSED' || status === 'ARCHIVED' || status === 'VOID'
}
